import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Card, CardContent, Grid, CircularProgress, TextField, Button, Snackbar, Alert } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import MapComponent from "../../components/MapComponent";
import { IViewDeliveryDTO } from "../../interfaces/Delivery";
import { getDeliveryOrderDetailsPublic, getDeliveryOrderDetailsPublicByOrder } from "../../services/Delivery";
import { formatDisplayDate, getOrderStatusStyle, formatOrderStatus } from "../../util/Util";

const TrackOrder = () => {
  const { packageId, orderId } = useParams<{ packageId?: string; orderId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchId, setSearchId] = useState(packageId || "");
  const [orderDetails, setOrderDetails] = useState<IViewDeliveryDTO | null>((location.state as any)?.orderData || null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [openError, setOpenError] = useState(false);

  useEffect(() => {
    if (orderDetails) return;
    (async () => {
      try {
        setLoading(true);
        let res = null;
        if (packageId) {
          res = await getDeliveryOrderDetailsPublic(packageId);
        } else if (orderId) {
          res = await getDeliveryOrderDetailsPublicByOrder(Number(orderId));
        }
        if (res) {
          setOrderDetails(res as IViewDeliveryDTO);
        }
      } catch {
        setOrderDetails(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [packageId, orderId]);

  const handleSearch = async () => {
    if (!searchId.trim()) return;
    setSearchLoading(true);
    try {
      const res = await getDeliveryOrderDetailsPublic(searchId);
      if (res) {
        setOrderDetails(res as IViewDeliveryDTO);
        navigate(`/track/${searchId}`, { replace: true });
      } else {
        setErrMsg("Package not found");
        setOpenError(true);
      }
    } catch {
      setErrMsg("Failed to fetch package details");
      setOpenError(true);
    } finally {
      setSearchLoading(false);
    }
  };

  const origin = orderDetails?.warehouseLatitude && orderDetails?.warehouseLongitude
    ? { lat: orderDetails.warehouseLatitude, lng: orderDetails.warehouseLongitude } : undefined;
  const partnerLocation = orderDetails?.deliveryPartnerLat && orderDetails?.deliveryPartnerLng
    ? { lat: orderDetails.deliveryPartnerLat, lng: orderDetails.deliveryPartnerLng } : undefined;
  const destination = orderDetails?.customerLatitude && orderDetails?.customerLongitude
    ? { lat: orderDetails.customerLatitude, lng: orderDetails.customerLongitude } : undefined;

  const routeDetails = [
    { name: "Warehouse", address: orderDetails?.warehouseAddress || "", type: "start" },
    { name: orderDetails?.customerName || "Customer", address: orderDetails?.address || "", type: "end" },
  ];

  return (
    <Box sx={{ width: "100%", height: "100vh", backgroundColor: "#F5F5F5", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2, py: 1, backgroundColor: "#fff", borderBottom: "1px solid", borderColor: "grey.200", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, position: "sticky", top: 0, zIndex: 100 }}>
        <Box component="img" src="/sidebar_Logo.png" alt="logo" sx={{ height: 60, objectFit: "contain", cursor: "pointer" }} onClick={() => navigate("/track")} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Enter Package ID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searchLoading}
            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "#005EFF", "&:hover": { bgcolor: "#0047CC" } }}
          >
            {searchLoading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Track"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, p: 1, overflowY: "auto" }}>
        <Paper sx={{ display: "flex", flexDirection: "column", borderRadius: "8px", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "grey.100", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Package details</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>Here are the full order details</Typography>
            </Box>
            {orderDetails?.status && (
              <Box sx={{ display: "inline-block", px: "0.7rem", py: "0.25rem", borderRadius: "6px", fontSize: "16px", fontWeight: 600, lineHeight: "16px", textAlign: "center", backgroundColor: getOrderStatusStyle(orderDetails.status).bg, color: getOrderStatusStyle(orderDetails.status).color, border: getOrderStatusStyle(orderDetails.status).border, minWidth: "120px" }}>
                {formatOrderStatus(orderDetails.status)}
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            {(loading || searchLoading) ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !orderDetails ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Typography color="text.secondary">Order not found.</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" fontWeight={600} mb={2}>Customer Details</Typography>
                    <Card sx={{ backgroundColor: "background.paper", boxShadow: 1, display: "flex", flexDirection: "column" }}>
                      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Box sx={{ backgroundColor: "primary.light", border: "2px solid", borderColor: "primary.main", borderRadius: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                            <PersonIcon sx={{ color: "primary.main" }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails.customerName || ''}</Typography>
                            <Typography variant="caption" color="grey.primary">Customer</Typography>
                          </Box>
                        </Box>
                        <Grid container spacing={2} sx={{ flex: 1 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Phone number</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails.phoneNumber || ''}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Delivery date</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatDisplayDate(orderDetails.deliveryDate || '')}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Address</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails.address || ''}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" fontWeight={600} mb={2}>Shipping Details</Typography>
                    <Card sx={{ backgroundColor: "background.paper", boxShadow: 1, display: "flex", flexDirection: "column" }}>
                      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          <Box sx={{ backgroundColor: "primary.light", border: "2px solid", borderColor: "primary.main", borderRadius: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                            <LocalShippingIcon sx={{ color: "primary.main" }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails.deliveryPartnerName || ''}</Typography>
                            <Typography variant="caption" color="grey.primary">Delivery Partner</Typography>
                          </Box>
                        </Box>
                        <Grid container spacing={2} sx={{ flex: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Phone number</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails.deliveryPartnerPhone || ''}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Out for Delivery Time</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {orderDetails.outForDeliveryTime ? `${formatDisplayDate(orderDetails.outForDeliveryTime)} ${new Date(orderDetails.outForDeliveryTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}` : ''}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, alignItems: "start" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="h6" fontWeight={600} mb={1}>Package Items</Typography>
                        <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {orderDetails.items?.map((item) => (
                              <Box key={item.orderItemId} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "8px", backgroundColor: item.packed ? "success.light" : "#F9FAFB", border: "1px solid", borderColor: item.packed ? "rgba(0, 178, 33, 0.2)" : "rgba(74,85,101,0.15)" }}>
                                <Box sx={{ width: 45, height: 45, borderRadius: "6px", backgroundColor: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <Box component="img" src={item.imageUrl || "/placeholder.png"} alt={item.itemName} sx={{ width: 35, height: 35, objectFit: "contain" }} onError={(e: any) => { e.target.style.display = "none"; }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{item.itemName}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>{item.barcode}</Typography>
                                </Box>
                                {item.packed ? (
                                  <CheckCircleIcon sx={{ color: "success.main", fontSize: "22px" }} />
                                ) : (
                                  <RadioButtonUncheckedIcon sx={{ color: "grey.500", fontSize: "22px" }} />
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="h6" fontWeight={600} mb={1}>Map Overview</Typography>
                        <Box sx={{ width: "100%", height: "300px", borderRadius: "8px", overflow: "hidden", mb: 1 }}>
                          <MapComponent height="100%" zoom={12} origin={origin} destination={destination} deliveryPartner={partnerLocation} />
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>Route Details</Typography>
                        <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none" }}>
                          <Box sx={{ position: "relative" }}>
                            {routeDetails.map((route, index) => (
                              <Box key={index} sx={{ display: "flex", gap: 2, pb: index < routeDetails.length - 1 ? 3 : 0 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "white", border: "3px solid", borderColor: route.type === "start" ? "primary.main" : "error.main", zIndex: 1 }} />
                                  {index < routeDetails.length - 1 && <Box sx={{ width: 3, flex: 1, backgroundColor: "primary.main", position: "absolute", top: 24, bottom: -24 }} />}
                                </Box>
                                <Box sx={{ flex: 1, pb: 1 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{route.name}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px", display: "block" }}>{route.address}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={() => setOpenError(false)}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{errMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackOrder;
